import React, { useEffect, useState } from 'react';
import './styles';
import { useSelector } from 'react-redux';
import {
	LISTING_CONTENT_TAB,
	LISTING_FLOOR_PLANS_TAB,
	LISTING_MATTERPORT_TAB,
	LISTING_PHOTOS_TAB,
	LISTING_PROPERTY_WEBSITE_TAB,
	LISTING_SERVICES_TAB,
	LISTING_VIDEOS_TAB
} from '../../../../utils/Config/MenuBodyTabs';
import Helmet from 'react-helmet';
import ListingServices from './ListingServices/ListingServices';
import UIMenuBody, { filterSelectedItem } from '../../../../components/UI/UIMenuBody';
import PLImage from '../../../../components/PL/PLImage';
import { PlusIcon } from '../../../../assets/icons';
import UIModal from '../../../../components/UI/UIModal';
import EditListingClientModal from '../../../../components/modals/EditListingClientModal/EditListingClientModal';
import ListingPhotos from './ListingPhotos';
import ListingFloorPlans from './ListingFloorplans';
import ListingVideos from './ListingVideos';
import ListingPropertyWebsite from './ListingPropertyWebsite';
import ListingMatterports from './ListingMatterport';
import { ContextMenu, ContextMenuTrigger, MenuItem } from 'react-contextmenu';
import UINotificationBanner from '../../../../components/UI/UINotificationBanner';
import { STATUS_LIVE, USER_ROLE_AGENT } from '../../../../utils/Config/Constants';
import UILoadingPage from '../../../../components/UI/UILoadingPage/UILoadingPage';
import PLPermissionBoundary from '../../../../components/Boundaries/PLPermissionBoundary/PLPermissionBoundary';
import { getPermissionForListing } from '../../../../utils/Helpers/ListingHelper';
import UIProperty from '../../../../components/UI/UIProperty';
import UIDownloadContent from '../../../../components/UI/UIDownloadShareContent/UIDownloadShareContent';
import { TYPES } from '../../../../components/UI/UIProperty/config';
import { getIsUserAdminSelector, getSignedInUserSelector } from '../../../../redux/selectors/CurrentUserSelectors';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { useLazyQuery } from '@apollo/react-hooks';
import { GET_INDIVIDUAL_LISTING } from './queries';
import { toast } from 'react-toastify';
import { CAMERA_LOADING_TYPE } from '../../../../components/UI/UILoadingPage/config';
import { getInitialRoute } from '../../../../utils/Helpers/UserHelpers';
import { ZendeskAPI } from 'react-zendesk';
import ListingContent from './ListingContent';

export default function IndividualListing({}: IndividualListingProps) {
	const signedInUser = useSelector(getSignedInUserSelector);
	const isUserAdmin = useSelector(getIsUserAdminSelector);
	const history = useHistory();
	const match = useRouteMatch();
	const location = useLocation();

	const [listing, setListing] = useState<ListingType>();
	const [showEditClientModal, setShowEditClientModal] = useState<boolean>(false);

	const [getListing, { data: listingData }] = useLazyQuery(GET_INDIVIDUAL_LISTING, {
		variables: { id: match.params['id'] }
	});

	useEffect(() => {
		(async () => {
			await getListing();
		})();
	}, []);

	useEffect(() => {
		(async () => {
			if (listingData) {
				const fetchedListing: ListingType = listingData['listing'];
				if (!fetchedListing) {
					toast.error(`No listing found for id: ${match.params['id']}`);
					history.push(getInitialRoute(signedInUser));
				}
				setListing(fetchedListing);
			}
		})();
	}, [listingData]);

	if (!listing) return <UILoadingPage />;

	const getTabs = () => {
		let listingArr: object[] = [];

		listingArr.push(LISTING_CONTENT_TAB);

		if (listing.services && listing.services.length > 0) {
			if (listing.services.filter((service) => service.status !== STATUS_LIVE).length > 0) {
				listingArr.splice(0, 0, LISTING_SERVICES_TAB);
			} else {
				listingArr.push(LISTING_SERVICES_TAB);
			}
		}

		// listingArr.push(LISTING_MARKETING_CENTER_TAB);
		listingArr.push(LISTING_PROPERTY_WEBSITE_TAB);

		return listingArr;
	};

	const renderAdditionalHeader = () => {
		let listingHasIncompleteServices = listing.services.filter((service) => service.status !== STATUS_LIVE).length > 0;
		const selectedPage = filterSelectedItem(getTabs(), location);

		if (listingHasIncompleteServices && [LISTING_PHOTOS_TAB, LISTING_VIDEOS_TAB, LISTING_FLOOR_PLANS_TAB, LISTING_MATTERPORT_TAB].indexOf(selectedPage) !== -1) {
			return (
				<UINotificationBanner
					onClick={() => history.push(`${match.url}?listing-services`)}
					backgroundColor={'var(--white-60)'}
					className='individualListing--notification-banner'
					text={'This listing has pending services. Once completed, your content will appear on this page.'}
					hoverText='View your services under the services tab'
				/>
			);
		}
		return null;
	};

	const renderEditClientModal = () => {
		const hideModal = async () => {
			await getListing();
			setShowEditClientModal(false);
		};
		return (
			<UIModal condition={showEditClientModal}>
				<EditListingClientModal listing={listing} hideModal={hideModal} />
			</UIModal>
		);
	};

	const renderMenuHeader = () => {
		let clientComponent;

		if (isUserAdmin) {
			clientComponent = (
				<div>
					{listing.client ? (
						<>
							<ContextMenuTrigger id='listing-client-context-menu' holdToDisplay={0}>
								<div className='individualListing__menuHeader__client'>
									<PLImage imageClassName='individualListing__menuHeader__client--image' src={listing.client.image} alt={'client'} />
									<p className='individualListing__menuHeader__client--text'>{listing.client.name}</p>
								</div>
							</ContextMenuTrigger>
							<div className='pl-separator' />
						</>
					) : (
						<>
							<div className='individualListing__menuHeader__client' onClick={() => setShowEditClientModal(true)}>
								<PLImage alt={'add'} className='individualListing__menuHeader__client--image' src={PlusIcon.white} />
								<p className='individualListing__menuHeader__client--text'>add a client</p>
							</div>
							<div className='pl-separator' />
						</>
					)}
				</div>
			);
		}

		const renderClientGoToRoute = () => {
			if (listing.client.role === USER_ROLE_AGENT) {
				return (
					<MenuItem className='general-title cursor plContextMenu--item' onClick={() => history.push(`/agent/${listing.client.id}`)}>
						Go to client
					</MenuItem>
				);
			} else {
				return (
					<MenuItem className='general-title cursor plContextMenu--item' onClick={() => history.push(`/photographer/${listing.client.id}`)}>
						Go to client
					</MenuItem>
				);
			}
		};

		return (
			<div className='individualListing__menuHeader'>
				<UIProperty type={TYPES.text} editOnClick={true} onFinishSavingAddress={getListing} property={listing.property || {}} />
				<div className='pl-separator' />
				<ContextMenu className='plContextMenu' id={'listing-client-context-menu'}>
					{renderClientGoToRoute()}
					<MenuItem className='general-title cursor plContextMenu--item' onClick={() => setShowEditClientModal(true)}>
						Edit client
					</MenuItem>
				</ContextMenu>
				<UIDownloadContent listing={listing} />
				{clientComponent}
			</div>
		);
	};

	const renderSelectedPage = () => {
		if (!listing) {
			return <p>no listing!</p>;
		}

		const selectedPage = filterSelectedItem(getTabs(), location);

		switch (selectedPage) {
			case LISTING_SERVICES_TAB:
				return <ListingServices listing={listing} />;
			case LISTING_PROPERTY_WEBSITE_TAB:
				return <ListingPropertyWebsite listingId={listing.id} />;
			case LISTING_CONTENT_TAB:
				return <ListingContent listingId={listing.id} />;
			case LISTING_PHOTOS_TAB:
				return <ListingPhotos listingId={listing.id} />;
			case LISTING_FLOOR_PLANS_TAB:
				return <ListingFloorPlans listingId={listing.id} />;
			case LISTING_VIDEOS_TAB:
				return <ListingVideos listingId={listing.id} />;
			case LISTING_MATTERPORT_TAB:
				return <ListingMatterports listingId={listing.id} />;
			default:
				return null;
		}
	};

	if (!signedInUser || !listing) {
		return <UILoadingPage type={CAMERA_LOADING_TYPE} />;
	}

	const title = `${(listing.property && listing.property.address) || 'Listing'} | Pineapple Labs`;

	return (
		<PLPermissionBoundary isGranted={getPermissionForListing(signedInUser, listing)}>
			<UIMenuBody menuHeader={renderMenuHeader()} menuItems={getTabs()} additionalHeader={renderAdditionalHeader()}>
				<>
					<Helmet>
						<title>{title}</title>
						<meta property='og:title' content={`${title} | Pineapple Labs`} />
					</Helmet>
					{renderSelectedPage()}
					{renderEditClientModal()}
				</>
			</UIMenuBody>
		</PLPermissionBoundary>
	);
}
